import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { PexelsPhoto, PexelsVideo, PexelsPagination, PexelsSearchParams, MediaType } from '../../components/pexels/types';

interface PexelsState {
  photos: PexelsPhoto[];
  videos: PexelsVideo[];
  pagination: PexelsPagination | null;
  loading: boolean;
  error: string | null;
  currentQuery: string;
  currentType: MediaType;
  currentPage: number;
  perPage: number;
}

const initialState: PexelsState = {
  photos: [],
  videos: [],
  pagination: null,
  loading: false,
  error: null,
  currentQuery: 'biriyani',
  currentType: 'photos',
  currentPage: 1,
  perPage: 20,
};

const pexelsSlice = createSlice({
  name: 'pexels',
  initialState,
  reducers: {
    // Search request
    searchPexelsRequest: (state, action: PayloadAction<PexelsSearchParams>) => {
      state.loading = true;
      state.error = null;
      state.currentQuery = action.payload.query;
      state.currentType = action.payload.type;
      state.currentPage = action.payload.page || 1;
      state.perPage = action.payload.per_page || 20;
    },
    // Search success
    searchPexelsSuccess: (
      state,
      action: PayloadAction<{
        data: PexelsPhoto[] | PexelsVideo[];
        pagination: PexelsPagination;
        type: MediaType;
        append?: boolean;
      }>
    ) => {
      state.loading = false;
      if (action.payload.type === 'photos') {
        if (action.payload.append) {
          state.photos = [...state.photos, ...(action.payload.data as PexelsPhoto[])];
        } else {
          state.photos = action.payload.data as PexelsPhoto[];
        }
      } else {
        if (action.payload.append) {
          state.videos = [...state.videos, ...(action.payload.data as PexelsVideo[])];
        } else {
          state.videos = action.payload.data as PexelsVideo[];
        }
      }
      state.pagination = action.payload.pagination;
      state.currentPage = action.payload.pagination.current_page;
    },
    // Search failure
    searchPexelsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Reset state
    resetPexels: (state) => {
      state.photos = [];
      state.videos = [];
      state.pagination = null;
      state.loading = false;
      state.error = null;
      state.currentQuery = 'pizza';
      state.currentType = 'photos';
      state.currentPage = 1;
      state.perPage = 20;
    },
    // Update search query
    updatePexelsQuery: (state, action: PayloadAction<string>) => {
      state.currentQuery = action.payload;
    },
    // Update media type
    updatePexelsType: (state, action: PayloadAction<MediaType>) => {
      state.currentType = action.payload;
    },
  },
});

export const {
  searchPexelsRequest,
  searchPexelsSuccess,
  searchPexelsFailure,
  resetPexels,
  updatePexelsQuery,
  updatePexelsType,
} = pexelsSlice.actions;

export default pexelsSlice.reducer;
